import _ from "lodash";
import React, { useState, useMemo, Fragment } from "react";
import classNames from "classnames";
import { Button } from "@toddle-edu/ds-web";
import classes from "./TagPractices.scss";
import Header from "./Header";
import StandardListWithSearch from "./StandardListWithSearch";
import {
  EVIDENCE_TYPE_UNIT,
  EVIDENCE_TYPE_POST,
  EVIDENCE_TYPE_ASSESSMENT,
  EVIDENCE_TYPE_PROGRESS_REPORT,
  ASSIGNMENT_TYPE_QUICK_TASK,
  ASSIGNMENT_TYPE_LE,
  ASSIGNMENT_TYPE_FORMATIVE,
  ASSIGNMENT_TYPE_SUMMATIVE,
  ASSIGNMENT_TYPE_PERFORMANCE_TASK,
  ASSIGNMENT_TYPE_PRE_ASSESSMENT,
  ASSIGNMENT_TYPE_SUPPLEMENTARY,
  ASSIGNMENT_TYPE_ASSIGNMENT_RESOURCE,
  ASSIGNMENT_TYPE_CGC_ASSESSMENT,
} from "Constants/stringConstants";

const Footer = props => {
  const {
    showBackButton,
    count,
    onBackButtonClick,
    t,
    onAddButtonClick,
    testId,
    isAddButtonDisabled,
  } = props;

  return (
    <div className={classes.footer}>
      <div className={classNames(classes.count, "text-label")}>
        {t("common:label_selected", {
          label: t("snp:practice_requirement_with_count", { count }),
        })}
      </div>
      <div className={classes.buttonContainer}>
        {showBackButton && (
          <Button
            size="medium"
            variant={"neutral"}
            type={"outlined"}
            onClick={onBackButtonClick}
            testId={`${testId}-back`}
            dsVersion={"2.0"}
          >
            {t(`common:back`)}
          </Button>
        )}
        <Button
          dsVersion={"2.0"}
          variant={"progressive"}
          type={"fill"}
          size="medium"
          disabled={isAddButtonDisabled}
          onClick={onAddButtonClick}
          testId={`${testId}-done`}
          shortcut={{
            code: "Enter",
            shiftKey: true,
          }}
        >
          {t(`common:done`)}
        </Button>
      </div>
    </div>
  );
};

const TagPractices = props => {
  const {
    itemTitle,
    t,
    goToPreviousPage,
    handleTaggedPractices,
    closeButtonElement,
    setId,
    practiceListsCollection,
    showBackButton,
    cycleDetails,
    testId,
    itemType,
  } = props;

  const { taggedPractices, disabledPractices } = practiceListsCollection;

  const [selectedPractices, setSelectedPractices] = useState(taggedPractices);

  const toggleSelectedPractices = ({ id, isSelected }) => {
    setSelectedPractices(selectedPractices => {
      if (isSelected) return [...selectedPractices, id];
      return _.filter(selectedPractices, practiceId => practiceId !== id);
    });
  };

  const onAddButtonClick = () => handleTaggedPractices({ selectedPractices });

  const evidenceTypeToEvidenceNameMapping = {
    [EVIDENCE_TYPE_UNIT]: "common:unit",
    [EVIDENCE_TYPE_ASSESSMENT]: "common:assessment",
    [EVIDENCE_TYPE_POST]: "common:post",
    [EVIDENCE_TYPE_PROGRESS_REPORT]: "common:report_card",
    [ASSIGNMENT_TYPE_QUICK_TASK]: "common:quick_task",
    [ASSIGNMENT_TYPE_LE]: "common:le_label",
    [ASSIGNMENT_TYPE_FORMATIVE]: "classRoom:formative_assessment",
    [ASSIGNMENT_TYPE_SUMMATIVE]: "classRoom:summative_assessment",
    [ASSIGNMENT_TYPE_PRE_ASSESSMENT]: "classRoom:pre_assessment",
    [ASSIGNMENT_TYPE_PERFORMANCE_TASK]: "common:performance_task",
    [ASSIGNMENT_TYPE_CGC_ASSESSMENT]: "common:assessment_label",
    [ASSIGNMENT_TYPE_SUPPLEMENTARY]: "common:supplementary_evidence",
    [ASSIGNMENT_TYPE_ASSIGNMENT_RESOURCE]: "common:quick_task",
  };

  const itemTypeLabel = t("common:lowercase", {
    text: t(evidenceTypeToEvidenceNameMapping[itemType]),
  });

  const itemDetails = {
    itemTypeLabel,
    itemTitle,
    messageLocale:
      itemType === EVIDENCE_TYPE_PROGRESS_REPORT
        ? "snp:progress_report_approved_for_practice_label"
        : "snp:evidence_approved_for_practice_label",
  };

  const isAddButtonDisabled = useMemo(() => {
    if (_.size(taggedPractices) !== _.size(selectedPractices)) return false;

    const taggedPracticesSet = new Set(taggedPractices);

    return _.every(selectedPractices, practiceId =>
      taggedPracticesSet.has(practiceId)
    );
  }, [taggedPractices, selectedPractices]);

  return (
    <Fragment>
      <div className={classes.container}>
        <Header t={t} closeButtonElement={closeButtonElement} />

        <StandardListWithSearch
          practiceListsCollection={{
            ..._.omit(practiceListsCollection, "taggedPractices"),
            selectedPractices,
          }}
          updateSelectedPractices={toggleSelectedPractices}
          setId={setId}
          itemDetails={itemDetails}
          cycleDetails={cycleDetails}
          t={t}
          testId={testId}
        />
      </div>

      <Footer
        showBackButton={showBackButton}
        count={_.size(selectedPractices) + _.size(disabledPractices)}
        onAddButtonClick={onAddButtonClick}
        onBackButtonClick={goToPreviousPage}
        isAddButtonDisabled={isAddButtonDisabled}
        t={t}
      />
    </Fragment>
  );
};

export default TagPractices;

TagPractices.defaultProps = {
  showBackButton: true,
};
